<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" :subTitle="$t('rfidAndFacerecognition.subtitle')" />
      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end">
          <McButtonBack data-me="master-fr-btn-back" class="me-2" href="/master" />
          <McButton
            data-me="master-fr-btn-upload"
            variant="secondary"
            class="me-2"
            icon="icon-outline_upload"
            :text="$t('global.action.upload')"
            @click="$router.push({ name: 'Master RFID Import' })"
          />
          <McButton
            data-me="master-fr-btn-open-rfid"
            variant="secondary"
            :text="$t('rfidAndFacerecognition.action.manage')"
            @click="openRFID()"
          />
        </div>
      </div>
    </div>
    <div class="table-responsive">
      <McGraphqlEditableTable
        data-me="master-fr-editable-table"
        trackBy="userId"
        ref="tableRfidAndFacerecognition"
        :key="tableKey"
        :fields="fields"
        :apiClient="query"
        :searchable="true"
        :isLoading="isLoading"
        :canCreate="false"
        :isPredefined="true"
        :canDelete="false"
        :hideCheckbox="true"
        :withCard="true"
        @handleChange="onEditRow"
        @handleEditCancel="onCancelEdit"
        :buttons="buttons"
        @beforeSubmit="handleBeforeSubmit"
      >
        <!-- Empty State -->
        <template #empty>
          <div class="d-flex align-items-center justify-content-center gap-2">
            <McEmptyState
              data-me="master-fr-empty-state"
              class="pb-4"
              :title="$t('rfidAndFacerecognition.empty.title')"
              :desc="$t('rfidAndFacerecognition.empty.subtitle')"
              :hasButton="hasAccessAll(['users:create', 'd5a-default-mdkaryawan:read'])"
              @buttonPrimaryAction="customHandlers.handleAddEmpty"
            />
          </div>
        </template>
        <!-- Face Recognition Status -->
        <template #cell(faceRecognition)="{ index, value, item }">
          <div class="d-flex align-items-center gap-2">
            <template v-if="index === editingRowId">
              <template
                v-if="
                  [
                    FACE_RECOGNITION_STATUS_LABEL.VALID,
                    FACE_RECOGNITION_STATUS_LABEL.WITH_PHOTO,
                    FACE_RECOGNITION_STATUS_LABEL.PENDING,
                  ].includes(item.faceRecognition?.status)
                "
              >
                <McButton
                  :data-me="`master-fr-btn-view-face-recognition-${index}`"
                  variant="secondary"
                  size="small"
                  icon="icon-fill_image"
                  :text="$t('rfidAndFacerecognition.photo.viewPhoto')"
                  @click="openPreviewPhoto(item)"
                />
                <i
                  role="button"
                  class="mc-fa icon-outline_trash-2 text-danger cursor-pointer"
                  :data-me="`master-fr-btn-delete-face-recognition-${index}`"
                  :disabled="isDeleting"
                  @click="handleDeleteFaceRecognition(item)"
                ></i>
              </template>
              <template v-else>
                <McButton
                  :data-me="`master-fr-btn-upload-face-recognition-${index}`"
                  variant="secondary"
                  size="small"
                  icon="icon-outline_upload"
                  text="Upload Foto"
                  @click="openUploadFaceRecognition(item)"
                />
              </template>
            </template>
            <template v-else>
              <i v-if="value.icon" class="mc-fa" :class="value.icon"></i>
              <span>{{ value.text }}</span>
            </template>
          </div>
        </template>

        <!-- Vehicles -->
        <template #vehicles="{ index, item }">
          <div class="d-flex flex-wrap gap-2">
            <template v-if="Array.isArray(item.vehiclesInitial) && item.vehiclesInitial.length">
              <span
                v-for="v in item.vehiclesInitial.slice(0, MAX_VISIBLE_VEHICLES)"
                :key="v.licensePlate"
                class="btn btn-primary mc-button mc-btn__small"
              >
                {{ v.licensePlate }}
              </span>
              <template v-if="item.vehicles.length > MAX_VISIBLE_VEHICLES">
                <span
                  :id="`vehicle-tooltip-${index}`"
                  class="mc-button mc-btn__small bg-green-500 text-white"
                >
                  +{{ item.vehicles.length - MAX_VISIBLE_VEHICLES }}
                </span>
                <b-tooltip :target="`vehicle-tooltip-${index}`" placement="top">
                  {{
                    item.vehiclesInitial
                      .slice(MAX_VISIBLE_VEHICLES)
                      .map((v) => v.licensePlate)
                      .join(', ')
                  }}
                </b-tooltip>
              </template>
            </template>
            <template v-else>
              <span>{{ $t('rfidAndFacerecognition.emptyVehicleList') }}</span>
            </template>
          </div>
        </template>
        <!-- Configuration Status -->
        <template #cell(configurationStatus)="{ value, index }">
          <div class="w-100 text-center d-flex justify-content-center align-items-center">
            <McButton
              :variant="value.variant"
              :text="value.text"
              :disabled="value.variant === 'secondary'"
              :data-me="`master-fr-btn-configuration-status-${index}`"
              size="small"
              :class="value.variant !== 'secondary' ? 'ms-1' : ''"
              @click="handleConfigurationStatus(value.userId)"
            >
              <template v-if="value.variant !== 'secondary'" #after-slot>
                <i class="mc-icon icon-fill_chevron-right" />
              </template>
            </McButton>
          </div>
        </template>
      </McGraphqlEditableTable>
    </div>

    <ModalRFID ref="modalRFID" data-me="master-fr-modal-rfid" />
    <ModalUploadFaceRecognition
      data-me="master-fr-modal-upload-face-recognition"
      ref="modalUploadFaceRecognition"
      @success="handleUploadSuccess"
    />
    <ModalPreviewPhoto ref="modalPreviewPhoto" data-me="master-fr-modal-preview-photo" />
    <ModalFilter
      class="ps-2 d-none"
      data-me="master-fr-btn-filter"
      ref="modalFilter"
      :activeFilters="filter"
      @applyFilters="onFilterChange"
    />
    <ModalConfigurationStatus
      data-me="master-fr-modal-configuration-status"
      ref="modalConfigurationStatus"
    />
    <ModalConfirmationDelete
      data-me="master-fr-modal-confirmation-delete"
      ref="modalConfirmationDelete"
      @delete-success="handleDeleteSuccess"
    />
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButton from '@/components/McButton'
import McButtonBack from '@/components/McButtonBack'
import McGraphqlEditableTable from '@/components/McGraphqlEditableTable'
import McEmptyState from '@/components/McEmptyState'
import { Helper } from '@/mixin/helper'
import {
  getFaceRecognitionStatus,
  FACE_RECOGNITION_STATUS,
  FACE_RECOGNITION_STATUS_LABEL,
  RFID_STATUS,
} from '@/helpers/face-recognition'
import { mapGetters } from 'vuex'
import ModalRFID from './components/ModalRFID.vue'
import ModalUploadFaceRecognition from './components/ModalUploadFaceRecognition.vue'
import ModalPreviewPhoto from './components/ModalPreviewPhoto.vue'
import ModalConfirmationDelete from './components/ModalConfirmationDelete.vue'
import ModalConfigurationStatus from './components/ModalConfigurationStatus.vue'
import ModalFilter from './components/ModalFilter.vue'
import {
  GET_EMPLOYEES_PAGINATED,
  GET_VEHICLES_BY_FEATURE_SUPPORT,
} from '@/services/api/master/rfid-facerecognition'
import { GET_RFIDS } from '@/services/api/master/rfid'
import { UPDATE_EMPLOYEE } from '@/services/api/master/employees'
import VehicleStatusConfiguration from './components/VehicleStatusConfiguration.vue'

const MAX_VISIBLE_VEHICLES = 3

export default {
  page: {
    title: 'Master',
    meta: [{ name: 'description', content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
    McButton,
    McButtonBack,
    McGraphqlEditableTable,
    McEmptyState,
    ModalRFID,
    ModalUploadFaceRecognition,
    ModalFilter,
    ModalPreviewPhoto,
    ModalConfigurationStatus,
    ModalConfirmationDelete,
  },

  mixins: [Helper],

  data() {
    return {
      FACE_RECOGNITION_STATUS_LABEL,
      VehicleStatusConfiguration,
      MAX_VISIBLE_VEHICLES,

      customHandlers: {
        handleAddEmpty: () => this.$router.push({ name: 'Master Employee Create' }),
      },

      filter: {
        faceRecognition: [],
        rfid: [],
        configurationStatus: [],
        uGroup: [],
        licensePlate: [],
      },

      isLoading: true,
      isDeleting: false,
      tableKey: 0,
      editingRowId: null,
      form: {
        rfid: null,
        vehicles: [],
        vehiclesInitial: [],
      },
      data: [],
    }
  },

  computed: {
    title() {
      return this.$t('rfidAndFacerecognition.title')
    },

    fields() {
      return [
        {
          key: 'fullname',
          label: this.$t('rfidAndFacerecognition.fieldsHeader.driver'),
          thStyle: { width: '150px', minWidth: '150px' },
          sortable: true,
          editable: false,
        },
        {
          key: 'userGroup.name',
          label: this.$t('rfidAndFacerecognition.fieldsHeader.groupUser'),
          thStyle: { width: '150px', minWidth: '150px' },
          sortable: true,
          editable: false,
          formatter: (val) => val ?? '-',
        },
        {
          key: 'email',
          label: this.$t('rfidAndFacerecognition.fieldsHeader.email'),
          thStyle: { width: '150px', minWidth: '150px' },
          sortable: true,
          editable: false,
        },
        {
          key: 'faceRecognition',
          label: this.$t('rfidAndFacerecognition.fieldsHeader.faceRecognition'),
          thStyle: { width: '150px', minWidth: '150px' },
          customColumn: true,
          sortable: true,
          editable: false,
          formatter: (val) => this.formatFaceRecognition(val),
        },
        {
          key: 'rfid',
          name: 'rfid',
          label: this.$t('rfidAndFacerecognition.fieldsHeader.rfId'),
          thStyle: { width: '250px', minWidth: '250px' },
          placeholder: this.$t('global.form.select') + ' RFID',
          isSortable: false,
          isEditable: true,
          type: 'select-resourceful',
          trackBy: 'rfid',
          optionsGetter: this.resourcefulQuery.rfidOptions,
          optionLabelFn: ({ item }) => item.rfid,
          getInitialObject: (item) => {
            return item.rfid ? { rfid: item.rfid.rfid } : null
          },
          customValueFn: (item) => item.rfid?.rfid ?? null,
          formatter: (val) => val?.rfid || this.$t('rfidAndFacerecognition.emptyRfid'),
        },
        {
          key: 'vehicles',
          name: 'vehicles',
          inputName: 'vehicleIds',
          label: this.$t('rfidAndFacerecognition.fieldsHeader.listVehicles'),
          thStyle: { width: '370px', minWidth: '370px' },
          placeholder: this.$t('global.form.select') + ' ' + this.$t('master.kendaraan'),
          isSortable: false,
          isEditable: true,
          customColumn: true,
          multiple: true,
          hasClose: false,
          visibleTagCount: 2,
          type: 'select-resourceful-v3',
          trackBy: 'id',
          optionsGetter: this.resourcefulQuery.vehicleOptions,
          optionLabelFn: ({ item }) => item.licensePlate,
          selectedLabel: 'licensePlate',
          getInitialObject: (item) => item.vehiclesInitial || [],
          customValueFn: (item) => item.vehicles ?? [],
          hasOptionMeta: true,
          metaRightComponent: VehicleStatusConfiguration,
        },
        {
          key: 'configurationStatus',
          label: this.$t('rfidAndFacerecognition.fieldsHeader.configurationStatus'),
          thStyle: { width: '120px', minWidth: '120px' },
          sortable: false,
          customColumn: true,
          editable: false,
          formatter: (val) => this.formatConfigurationStatus(val),
        },
      ]
    },

    resourcefulQuery() {
      return {
        rfidOptions: async (opts) => {
          try {
            const res = await this.$apollo.query({
              query: GET_RFIDS,
              variables: {
                filter: {
                  pagination: { show: opts.limit, page: opts.page },
                  search: opts.search,
                },
              },
              fetchPolicy: 'no-cache',
            })

            const data = res.data.getRfids.getRfids
            return {
              metadata: {
                total_page: Math.ceil(data.pagination.total / data.pagination.show),
                total_count: data.pagination.total,
                count: data.pagination.total,
                page: opts.page,
              },
              data: { paginated_result: data.data },
            }
          } catch (err) {
            console.error('Error fetching rfids:', err)
            return { data: [], pagination: { total: 0, page: 1, show: 10 } }
          }
        },

        vehicleOptions: async (opts) => {
          let result = {}
          try {
            const feature = 'IDENTIFICATION'
            const res = await this.$apollo.query({
              query: GET_VEHICLES_BY_FEATURE_SUPPORT,
              variables: {
                filter: {
                  pagination: {
                    show: opts.limit || 10,
                    page: opts.page || 1,
                  },
                  search: opts.search,
                  feature: feature,
                  withUnsupportedVehicles: true,
                },
              },
              fetchPolicy: 'no-cache',
            })

            const data = res?.data?.vehiclesByFeatureSupport?.vehiclesByFeatureSupport

            result = {
              metadata: {
                total_page: Math.ceil(data.pagination.total / data.pagination.show),
                total_count: data.pagination.total,
                count: data.pagination.total,
                page: opts.page,
              },
              data: {
                paginated_result: data.data,
              },
            }
          } catch (err) {
            console.error('Error fetching vehicles:', err)
            result = {
              data: {
                paginated_result: [],
              },
              pagination: { total: 0, page: 1, show: 10 },
            }
          }
          return result
        },
      }
    },

    query() {
      return {
        get: async (opts) => {
          return await this.getData(opts)
        },

        update: async (id, oldData, newData) => {
          try {
            const hasPhoto = oldData?.photograph
            let vehicleIds = newData?.vehicleIds || []

            if (!hasPhoto && newData?.rfid === null) {
              const employeeForModal = {
                userId: id,
                photograph: oldData?.photograph,
                rfid: oldData?.rfid,
                vehicles: oldData?.vehicles || [],
              }
              this.$refs.modalConfirmationDelete.show(employeeForModal, 'rfid')
              return
            }
            const oldDataVehicle = oldData?.vehicles || []
            const res = await this.$apollo.mutate({
              mutation: UPDATE_EMPLOYEE,
              variables: {
                input: {
                  userId: id,
                  oldData: {
                    identification: {
                      rfid: oldData?.rfid?.rfid ?? null,
                      vehicleIds: oldDataVehicle,
                    },
                  },
                  newData: {
                    identification: {
                      rfid: newData?.rfid ?? null,
                      vehicleIds: vehicleIds,
                    },
                  },
                },
              },
            })
            if (!res.data.updateEmployee.isSuccessful) {
              this.$mcToast.error(
                res.data.updateEmployee.message || this.$t('global.alert.text.failed')
              )
              return
            }
            this.editingRowId = null
            return res.data.updateEmployee.isSuccessful
          } catch (error) {
            console.error('Error updating employee:', error)
            this.$mcToast.error(
              res?.data?.updateEmployee?.message || this.$t('global.alert.text.failed')
            )
            return false
          }
        },
      }
    },

    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),

    buttons() {
      return [
        {
          variant: 'secondary',
          class: 'me-2',
          id: 'master-fr-btn-filter',
          text: 'Filter',
          icon: 'icon-outline_filter',
          handleClick: () => {
            this.openModalFilter()
          },
        },
      ]
    },
  },

  methods: {
    handleBeforeSubmit(data, currentItem, resolve) {
      const hasRfid = !!data.rfid || !!currentItem?.rfid?.rfid
      const hasFaceRecognition = !!currentItem?.photograph
      const vehiclesFromForm = Array.isArray(data.vehicleIds)
        ? data.vehicleIds.length > 0
        : !!data.vehicleIds
      const vehiclesFromItem = Array.isArray(currentItem?.vehicles)
        ? currentItem.vehicles.length > 0
        : false
      const hasVehicles = vehiclesFromForm || vehiclesFromItem

      if (hasVehicles) {
        if (!hasRfid && !hasFaceRecognition) {
          this.$mcToast.error(
            this.$t('rfidAndFacerecognition.flashMessage.validation.vehicleNeedsIdentity')
          )
          resolve({ success: false })
          return
        }
      }
      resolve({ success: true })
    },
    formatFaceRecognition(val) {
      const successText = this.$t('rfidAndFacerecognition.photo.success')
      const availableText = this.$t('rfidAndFacerecognition.photo.available')
      const emptyText = this.$t('rfidAndFacerecognition.photo.empty')
      const failedText = this.$t('rfidAndFacerecognition.photo.failed')
      const pendingText = this.$t('rfidAndFacerecognition.photo.pending')

      if (!val?.photoUrl) {
        return {
          text: emptyText,
          icon: 'icon-fill_close-circle text-secondary',
        }
      }

      const statusMap = {
        [FACE_RECOGNITION_STATUS_LABEL.VALID]: {
          text: successText,
          icon: 'icon-fill_checkmark-circle text-success',
        },
        [FACE_RECOGNITION_STATUS_LABEL.INVALID]: {
          text: failedText,
          icon: 'icon-fill_close-circle text-danger',
        },
        [FACE_RECOGNITION_STATUS_LABEL.PENDING]: {
          text: pendingText,
          icon: '',
        },
        [FACE_RECOGNITION_STATUS_LABEL.WITH_PHOTO]: {
          text: availableText,
          icon: '',
        },
      }

      return (
        statusMap[val.status] || {
          text: emptyText,
          icon: 'icon-fill_close-circle text-secondary',
        }
      )
    },

    formatConfigurationStatus(val) {
      const vehicleCount = val?.vehicleTotal ?? 0
      const configurationCount = val?.configurationTotal ?? 0

      let variant = 'secondary'
      if (vehicleCount > 0 && configurationCount === vehicleCount) {
        variant = 'success'
      } else if (vehicleCount > 0) {
        variant = 'warning'
      }

      return {
        userId: val?.userId,
        variant,
        text: `${configurationCount}/${vehicleCount}`,
      }
    },

    mapEmployee(item) {
      const identification = item?.identification
      const methods = identification?.methods || {}
      const vehicles = identification?.vehicles || []
      const faceStatuses = vehicles
        .map((v) => v?.methodStatuses?.faceRecognition)
        .filter((s) => s != null)

      const hasPhoto = !!item.photograph

      // face status mapping
      const faceRecognitionStatus = getFaceRecognitionStatus({ hasPhoto, faceStatuses })

      // configuration total
      const configurationTotal = vehicles.filter((v) => {
        const face = v?.methodStatuses?.faceRecognition
        const rfid = v?.methodStatuses?.rfid

        if (face === null && rfid === null) return false

        const faceValid = face === FACE_RECOGNITION_STATUS.VALID || face === null
        const rfidValid = rfid === RFID_STATUS.SUCCESS || rfid === null

        return faceValid && rfidValid
      }).length

      const mappedVehicles = vehicles.map((v) => ({
        id: v.vehicleId,
        licensePlate: v.licensePlate,
        methodStatuses: v.methodStatuses,
      }))

      const userGroup = item.groups?.[0]
        ? { id: item.groups[0].id, name: item.groups[0].name }
        : null
      return {
        userId: item.userId,
        address: item.address,
        fullname: item.fullname,
        email: item.email,
        photograph: item.photograph,
        faceRecognition: {
          status: faceRecognitionStatus,
          userId: item.userId,
          photoUrl: item.photograph || null,
        },
        userGroup: userGroup,
        rfid: methods.rfid ? { rfid: methods.rfid } : null,
        vehicles: mappedVehicles.map((v) => v.id),
        vehiclesInitial: mappedVehicles,
        configurationStatus: {
          userId: item.userId,
          vehicleTotal: vehicles.length,
          configurationTotal,
        },
      }
    },

    async getData(opts) {
      this.isLoading = true
      try {
        const identification = {}
        if (this.filter.licensePlate?.length) identification.vehicleIds = this.filter.licensePlate
        if (this.filter.rfid?.length) identification.rfidStatus = this.filter.rfid
        if (this.filter.faceRecognition?.length)
          identification.faceRecognitionStatus = this.filter.faceRecognition
        if (this.filter.configurationStatus?.length)
          identification.groupedStatus = this.filter.configurationStatus

        const additionalFilters = {}
        if (Object.keys(identification).length) additionalFilters.identification = identification
        if (this.filter.uGroup?.length) additionalFilters.ugroupId = this.filter.uGroup

        const res = await this.$apollo.query({
          query: GET_EMPLOYEES_PAGINATED,
          variables: {
            filter: {
              isActive: true,
              isDriver: true,
              orderBy: opts.orderBy,
              search: opts.search,
              pagination: {
                page: opts.pagination.page,
                show: opts.pagination.show,
              },
              ...additionalFilters,
            },
          },
          fetchPolicy: 'no-cache',
        })

        const response = res?.data?.employeesPaginated

        if (!response?.isSuccessful) {
          this.$mcToast.error(response?.message || 'Error fetching employees')
          return { data: [], pagination: { total: 0, page: 1, show: 10 } }
        }

        const employees = res.data.employeesPaginated.employeesPaginated
        const data = employees.data.map(this.mapEmployee)
        this.data = data

        return {
          data,
          pagination: {
            total: employees.pagination.total,
            page: employees.pagination.page,
            show: employees.pagination.show,
          },
        }
      } catch (error) {
        console.error('Error fetching employees:', error)
        this.$mcToast.error('Error fetching employees: ' + error.message)
        return { data: [], pagination: { total: 0, page: 1, show: 10 } }
      } finally {
        this.isLoading = false
      }
    },

    openRFID() {
      this.$refs.modalRFID.show()
    },

    openModalFilter() {
      this.$refs.modalFilter.show()
    },

    openUploadFaceRecognition(item) {
      this.$refs.modalUploadFaceRecognition.show(item)
    },

    openPreviewPhoto(item) {
      this.$refs.modalPreviewPhoto.show(item)
    },

    handleConfigurationStatus(driverId) {
      this.$refs.modalConfigurationStatus.show(driverId)
    },

    async handleDeleteFaceRecognition(employee) {
      this.isDeleting = true
      try {
        const hasRfid = employee?.rfid?.rfid

        if (!hasRfid) {
          this.$refs.modalConfirmationDelete.show(employee, 'photo')
          return
        }
        await this.$refs.modalConfirmationDelete.deletePhoto(employee)
      } catch (error) {
      } finally {
        this.isDeleting = false
      }
    },

    handleUploadSuccess() {
      this.refreshTable()
    },
    handleDeleteSuccess() {
      this.refreshTable()
    },

    onFilterChange(value) {
      this.filter.faceRecognition = this.findFilterValue(value, 'faceRecognition') || null
      this.filter.rfid = this.findFilterValue(value, 'rfid') || null
      this.filter.configurationStatus = this.findFilterValue(value, 'configurationStatus') || null
      this.filter.licensePlate = this.findFilterValue(value, 'licensePlate') || []
      this.filter.uGroup = this.findFilterValue(value, 'uGroup') || []
      this.tableKey++
    },

    onEditRow(data) {
      this.editingRowId = data.index
    },

    onCancelEdit() {
      this.editingRowId = null
    },
    refreshTable() {
      setTimeout(() => {
        if (this.$refs.tableRfidAndFacerecognition) {
          this.$refs.tableRfidAndFacerecognition.getData({
            limit: this.$refs.tableRfidAndFacerecognition.limit,
            page: this.$refs.tableRfidAndFacerecognition.page,
          })
          this.$refs.tableRfidAndFacerecognition.isEditingRow = null
          this.editingRowId = null
        }
      }, 500)
    },
  },
}
</script>
